/* Copyright (c) 2025 Xperi. All Rights Reserved. */

/**
 * This module defines the `GalleryScreenComponent` class and related methods for handling various
 * Gallery screen functionalities.
 * It imports necessary utils to manage first Aired date, season and Episode information.
 * Generate the grid for gallery items of a single page used in pagination view of GalleryScreen.
 * The module integrates with various components and services to provide a comprehensive
 * Gallery Screen View.
 */

import React from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { VideoResolution } from 'client-code/actions';
import {
    EpisodeVodItem,
    ExpiryStatus,
    MovieVodItem,
    ScreenType,
    VodItem,
} from 'client-code/vod-browse';

import { BrandingUiInfoKeys } from '../entities';
import { lruSet } from '../utils/LruCacheUtils';
import { ErrorTypes, VodBrowseError } from '../error';
import { DefaultServicesClientImpl } from '../impls';
import { VodInteractor } from '../interactors';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { TGalleryGridActivePageResponse, TGalleryGridResponse } from '../types';
import { DateTime, UniqueIdentifier } from '../utils';

export class GalleryScreenComponent {
    /*
     * Formats season and episode information.
     *
     * @param seasonNumber - The season number.
     * @param episodeNumber - The episode number.
     * @returns A formatted string like "S1 E5" or an empty string if both are missing.
     */

    // Properties for generating grid of a single page in gallery screen.
    private readonly TAG: string = 'GalleryScreenComponent';

    private readonly TOTAL_NUMBERS_OF_MOVIES_ITEMS_IN_GRID: number = 15;

    private readonly TOTAL_NUMBERS_OF_SERIES_ITEMS_IN_GRID: number = 9;

    private mScreenType: string;

    private mMaxItemsInPage: number;

    private mColumnLimitForMovies: number;

    private mColumnLimitForSeries: number;

    private mColumnLimit: number;

    private mDefaultServices: DefaultServicesClientImpl;

    // Static pagination state
    private static mCurrentPage: number = 0;

    private static mTotalItems: number = 0;

    private static mNoItems: boolean = false;

    private static cachedGalleryItems: Map<number, VodItem[]> = new Map();

    private static currentParentVodItemId: string | null = null;

    /**
     * Constructs a GalleryScreenComponent instance and initializes member variables.
     * @param screenType - TODO: Replace with actual screen type logic.
     */
    public constructor(screenType: string = 'posterBrowseMixed') {
        this.mScreenType = screenType;
        this.mMaxItemsInPage =
            this.mScreenType === ScreenType.PosterBrowseMovie
                ? this.TOTAL_NUMBERS_OF_MOVIES_ITEMS_IN_GRID
                : this.TOTAL_NUMBERS_OF_SERIES_ITEMS_IN_GRID;
        this.mColumnLimitForMovies = 5;
        this.mColumnLimitForSeries = 3;
        this.mColumnLimit =
            this.mScreenType.toLowerCase() === ScreenType.PosterBrowseMovie.toLowerCase()
                ? this.mColumnLimitForMovies
                : this.mColumnLimitForSeries;
        this.mDefaultServices = new DefaultServicesClientImpl();
    }

    // eslint-disable-next-line class-methods-use-this
    private getSeasonEpisode(seasonNumber?: number, episodeNumber?: number): string {
        const season = seasonNumber ? `S${seasonNumber}` : '';
        const episode = episodeNumber ? `E${episodeNumber}` : '';
        return `${season} ${episode}`.trim();
    }

    /**
     * Formats a date string into "DD/MM/YY" format.
     *
     * @param date - The date string to format.
     * @returns A formatted date string or an empty string if the date is invalid.
     */

    // eslint-disable-next-line class-methods-use-this
    private getFirstAiredDate(date: string): string {
        if (!date) return '';

        const dateTime = new DateTime(date);
        return dateTime ? dateTime.strftime('%D/%M/%y') : '';
    }

    private videoResolutionIcon = (
        videoResolution: VideoResolution | undefined,
    ): React.ReactNode => {
        if (!videoResolution) return null;

        const resolutionClassMap: Record<VideoResolution, string> = {
            [VideoResolution.Uhd]: 'icon-uhd',
            [VideoResolution.Hd]: 'icon-hd',
            [VideoResolution.Sd]: 'icon-sd',
        };

        return resolutionClassMap[videoResolution] || null;
    };

    private expiryStatusIcon = (expiryStatus: ExpiryStatus | undefined): React.ReactNode => {
        if (!expiryStatus) return null;

        const expiryClassMap: Record<ExpiryStatus, string> = {
            [ExpiryStatus.ExpiresIn24Hours]: 'entitlement-expiry-24-hours',
            [ExpiryStatus.ExpiresIn72Hours]: 'entitlement-expiry-72-hours',
        };

        return expiryClassMap[expiryStatus] || null;
    };

    public getMediaAttributes(item: EpisodeVodItem | MovieVodItem): {
        seasonEpisode: string;
        airDate: string;
        resolutionIcon: React.ReactNode;
        expiryIcon: React.ReactNode;
    } {
        if (!item) {
            return {
                seasonEpisode: '',
                airDate: '',
                resolutionIcon: null,
                expiryIcon: null,
            };
        }

        const seasonEpisode = this.getSeasonEpisode(
            (item as EpisodeVodItem).seasonNumber,
            (item as EpisodeVodItem).episodeNumber,
        );
        const airDate = this.getFirstAiredDate((item as EpisodeVodItem).firstAirDate || '');
        const resolutionIcon = item.videoResolution
            ? this.videoResolutionIcon(item.videoResolution)
            : null;
        const expiryIcon = item.expiryStatus ? this.expiryStatusIcon(item.expiryStatus) : null;

        return {
            seasonEpisode,
            airDate,
            resolutionIcon,
            expiryIcon,
        };
    }

    /**
     * Static getter for current page.
     */
    public static getCurrentPage(): number {
        return GalleryScreenComponent.mCurrentPage;
    }

    /**
     * Static getter for total items.
     */
    public static getTotalItems(): number {
        return GalleryScreenComponent.mTotalItems;
    }

    /**
     * Static getter for no items flag.
     */
    public static getNoItems(): boolean {
        return GalleryScreenComponent.mNoItems;
    }

    /**
     * Getter for max items in a page.
     * @returns The maximum number of items per page for the current screen type.
     */
    public getMaxItemsInPage(): number {
        return this.mMaxItemsInPage;
    }

    /**
     * Calculates grid focus parameters for pagination handling.
     * @param response - The grid response object.
     * @param focusedItem - The currently focused item (column).
     * @returns An object containing total items, column limit, row limit
     * and last column which helps to set focus on items.
     */
    private getGridFocusParams(response: TGalleryGridResponse, focusedItem: { column: number }) {
        const totalItemsInCurrentPage = Array.isArray(response.items) ? response.items.length : 0;
        const colLimit = this.mColumnLimit;
        const rowLimit = Math.ceil(totalItemsInCurrentPage / colLimit);
        const lastcolumn =
            focusedItem.column > (totalItemsInCurrentPage - 1) % colLimit
                ? (totalItemsInCurrentPage - 1) % colLimit
                : focusedItem.column;
        return { totalItemsInCurrentPage, colLimit, rowLimit, lastcolumn };
    }

    /**
     * Handles errors during Gallery screen data fetching and returns a standardized error response.
     * @param error - The error object.
     * @param pageNumber - The page number where the error occurred.
     * @returns A standardized grid response with error information.
     */
    private getGalleryScreenErrorResponse(
        error: any,
        pageNumber: number,
    ): TGalleryGridResponse | undefined {
        if (pageNumber !== GalleryScreenComponent.mCurrentPage) return;
        const vodBrowseServiceError = error?.response?.data;
        const vodBrowseError = new VodBrowseError(
            vodBrowseServiceError?.code,
            vodBrowseServiceError?.message,
            Locale.getString(LocaleKeys.GALLERY_SCREEN_OVERLAY_ERROR_MESSAGE),
        );
        const subErrorCode = vodBrowseError.getCode();
        const errorCode = `${ErrorTypes.GALLERY_SCREEN_ERROR_RESPONSE_C1700}-${subErrorCode}`;
        const description = vodBrowseError
            .getDescription()
            .replace('ERROR_CODE', errorCode)
            .replace(
                'CUSTOMER_SUPPORT_NAME',
                this.mDefaultServices.getBrandingUiInfoForKey(
                    BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
                ),
            )
            .replace(
                'PHONE_CUSTOMER_SUPPORT_NUMBER',
                this.mDefaultServices.getBrandingUiInfoForKey(
                    BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
                ),
            );
        vodBrowseError.setDescription(description);
        Diagnostics.error(
            this.TAG,
            `Invalid Data in Gallery Screen for page ${pageNumber}.`,
            vodBrowseError,
        );

        return {
            items: [],
            currentPage: pageNumber,
            totalItems: 0,
            hasError: true,
            errorCode,
            parentTitle: Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE),
        };
    }

    /**
     * Helps to reset the static pagination state for the gallery screen.
     */
    // eslint-disable-next-line class-methods-use-this
    public reset(): void {
        GalleryScreenComponent.mCurrentPage = 0;
        GalleryScreenComponent.mTotalItems = 0;
        GalleryScreenComponent.mNoItems = false;
        GalleryScreenComponent.cachedGalleryItems.clear();
        GalleryScreenComponent.currentParentVodItemId = null;
    }

    // check if full page already cached with given offset
    /**
     * Checks if a specific range of gallery items is cached.
     * Verifies that all items within the specified page exist in the cache.
     * Returns false if any item in the page is not cached.
     * @param page - The page number to check in the cache.
     * @returns `true` if all items in the range are cached, otherwise `false`.
     */
    private static isPageCached(page: number): boolean {
        return GalleryScreenComponent.cachedGalleryItems.has(page);
    }

    /**
     * Get the grid data for a given parent VOD item ID and page number.
     * @param parentVodItemId - The parent VOD item ID.
     * @param page - The page number to fetch.
     * @returns A promise resolving to the grid response object.
     */
    public async getGalleryGridData(
        parentVodItemId: string,
        page: number,
    ): Promise<TGalleryGridResponse> {
        // Invalidate cache if parent changes
        if (GalleryScreenComponent.currentParentVodItemId !== parentVodItemId) {
            this.reset(); // clears all data including cached items & parentVodItemId
            GalleryScreenComponent.currentParentVodItemId = parentVodItemId; // set new parent
        }

        const vodServices = new VodInteractor();
        const offset = page * this.mMaxItemsInPage;
        const limit = this.mMaxItemsInPage;

        if (GalleryScreenComponent.isPageCached(page)) {
            const items = GalleryScreenComponent.cachedGalleryItems.get(page)!;
            GalleryScreenComponent.mCurrentPage = page;
            return {
                items,
                currentPage: page,
                totalItems: GalleryScreenComponent.mTotalItems,
                hasError: false,
                parentTitle: '',
            };
        }

        try {
            const response = await vodServices.getVodBrowseData({
                parentVodItemId,
                offset,
                limit,
            });
            const { parentTitle, vodItemList } = response?.vodItemList || [];
            const totalItems = response.totalCount || 0;

            GalleryScreenComponent.mTotalItems = totalItems;
            GalleryScreenComponent.mCurrentPage = page;

            if (!vodItemList || vodItemList.length === 0) {
                GalleryScreenComponent.mNoItems = true;
                return {
                    items: [],
                    currentPage: page,
                    totalItems,
                    hasError: false,
                    parentTitle: parentTitle || '',
                };
            }

            // Populate cache (capped to prevent unbounded growth)
            lruSet(GalleryScreenComponent.cachedGalleryItems, page, vodItemList, 50);

            return {
                items: vodItemList,
                currentPage: page,
                totalItems,
                hasError: false,
                parentTitle: parentTitle || '',
            };
        } catch (_error) {
            Diagnostics.error(this.TAG, 'getGalleryGridData', _error);
            return (
                this.getGalleryScreenErrorResponse(_error, page) || {
                    items: [],
                    currentPage: page,
                    totalItems: 0,
                    hasError: true,
                    errorCode: String(_error),
                    parentTitle: '',
                }
            );
        }
    }

    /**
     * Get the initial gallery page for the given parent VOD item ID.
     * @param parentVodItemId - The parent VOD item ID.
     * @param page - The page number to load (defaults to 0).
     * @returns A promise resolving to the active page response or null.
     */
    public getInitialGalleryPage = async (
        parentVodItemId: string,
        page: number = 0,
    ): Promise<TGalleryGridActivePageResponse | null> => {
        const response = await this.getGalleryGridData(parentVodItemId, page);

        const gridParams = this.getGridFocusParams(response, {
            column: 0,
        });

        return { response, gridParams };
    };

    /**
     * Fetches the next gallery page for pagination.
     * @param parentVodItemId - The parent VOD item ID.
     * @param focusedItem - The currently focused item (column).
     * @returns A promise resolving to the active page response or null.
     */
    public getNextGalleryPage = async (
        parentVodItemId: string,
        focusedItem: { column: number },
    ): Promise<TGalleryGridActivePageResponse | null> => {
        const nextPage = GalleryScreenComponent.mCurrentPage + 1;
        const nextOffset = nextPage * this.mMaxItemsInPage;

        if (nextOffset >= GalleryScreenComponent.mTotalItems) {
            Diagnostics.debug(this.TAG, 'Already at last page, not fetching next page.');
            Log.d(this.TAG, 'Already at last page, not fetching next page.');
            return null;
        }
        const response = await this.getGalleryGridData(parentVodItemId, nextPage);
        const gridParams = this.getGridFocusParams(response, focusedItem);
        return { response, gridParams };
    };

    /**
     * Fetches the previous gallery page for pagination.
     * @param parentVodItemId - The parent VOD item ID.
     * @param focusedItem - The currently focused item (column).
     * @returns A promise resolving to the active page response or null.
     */
    public getPrevGalleryPage = async (
        parentVodItemId: string,
        focusedItem: { column: number },
    ): Promise<TGalleryGridActivePageResponse | null> => {
        const prevPage = Math.max(GalleryScreenComponent.mCurrentPage - 1, 0);
        if (prevPage === GalleryScreenComponent.mCurrentPage) {
            Diagnostics.debug(this.TAG, 'Already at first page in Gallery Screen');
            Log.d(this.TAG, 'Already at first page in Gallery Screen');
            return null;
        }
        const response = await this.getGalleryGridData(parentVodItemId, prevPage);
        const gridParams = this.getGridFocusParams(response, focusedItem);

        return { response, gridParams };
    };

    /**
     * Returns the total number of items seen so far by pagination.
     * @param currentPage - The current page index (zero-based).
     * @returns Number of items seen so far.
     */
    public static getItemsSeenSoFar(currentPage: number): number {
        const pageSize = new GalleryScreenComponent().getMaxItemsInPage();
        const totalItems = GalleryScreenComponent.mTotalItems;
        return Math.min((currentPage + 1) * pageSize, totalItems);
    }

    /**
     * Returns the number of items left to show in the gallery after the current page.
     * @returns Number of remaining items.
     */
    public static getItemsLeftInGallery(): number {
        const pageSize = new GalleryScreenComponent().getMaxItemsInPage();
        const totalItems = GalleryScreenComponent.mTotalItems;
        const shownItems = (GalleryScreenComponent.mCurrentPage + 1) * pageSize;
        return Math.max(totalItems - shownItems, 0);
    }
}
